import type { CardRole, SupportCardId } from '@archon3d/core';
import { useEffect, useState } from 'react';
import { usePortraits } from '../editor/portraits';
import type { ClockView } from '../game/countdown';
import type { CardClashView, SessionSnapshot } from '../game/session';
import { sideName } from '../game/sideName';
import { chanceNow, storyMoment } from '../game/cardStory';
import { CardReveal } from './CardReveal';

interface Props {
  snapshot: SessionSnapshot;
  clash: CardClashView;
  onCommit: (card: SupportCardId | null, role?: CardRole) => void;
  /** The people here have read the revealed cards. */
  onContinue: () => void;
  onMenu: () => void;
  onRules: () => void;
}

const HEXES = Array.from({ length: 5 }, (_, row) => row - 2).flatMap((r) => {
  const qMin = Math.max(-2, -r - 2);
  const qMax = Math.min(2, -r + 2);
  return Array.from({ length: qMax - qMin + 1 }, (_, index) => ({ q: qMin + index, r }));
});

export function CardBattleOverlay({
  snapshot,
  clash,
  onCommit,
  onContinue,
  onMenu,
  onRules,
}: Props) {
  const portraits = usePortraits(snapshot.ctx.roster);
  const clock = snapshot.cardClock ?? null;
  // A reveal that waits for the people here: Continue, Enter or a tap anywhere but a button.
  const holding = clash.phase === 'reveal' && clash.hold === 'continue';
  useEffect(() => {
    if (!holding) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' || event.target instanceof HTMLButtonElement) return;
      event.preventDefault();
      onContinue();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [holding, onContinue]);
  return (
    <div
      className={`card-battle${clash.phase === 'reveal' ? ' revealing' : ''}`}
      role="dialog"
      aria-label="Card combat"
      onClick={
        holding
          ? (event) => {
              if (!(event.target as HTMLElement).closest('button')) onContinue();
            }
          : undefined
      }
    >
      <div className="card-arena">
        <div className="card-arena-head">
          <div className="card-battle-menu row">
            <button onClick={onMenu} data-testid="card-menu">
              Menu
            </button>
            <button onClick={onRules} data-testid="card-rules">
              Rules
            </button>
          </div>
          <div className="card-arena-title">CARD COMBAT</div>
        </div>
        <div className="card-hex-board" aria-hidden="true">
          {HEXES.map(({ q, r }) => (
            <span
              className="card-hex"
              key={`${q},${r}`}
              style={{ left: 140 + (q + r / 2) * 61, top: 115 + r * 52 }}
            />
          ))}
        </div>
        <div className="main-cards">
          {clash.participants.map((participant) => {
            const score = clash.scores?.find((entry) => entry.pieceId === participant.pieceId);
            const level =
              score?.level ??
              participant.level ??
              snapshot.ctx.cardLevels?.[participant.faction] ??
              'expert';
            // While the clash is told, the chance as it stands; the outcome once it is over.
            const moment = storyMoment(clash.story);
            const chance = chanceNow(clash.story, participant.pieceId) ?? score?.probability;
            const outcome = score?.won !== undefined && moment.over;
            return (
              <article
                className={`battle-card main-card${outcome ? (score!.won ? ' winner' : ' defeated') : ''}`}
                key={participant.pieceId}
              >
                <div className="card-portrait">
                  {portraits[participant.character] ? (
                    <img src={portraits[participant.character]} alt={participant.name} />
                  ) : (
                    <span>{participant.name.slice(0, 1)}</span>
                  )}
                </div>
                <h2>{participant.name}</h2>
                <p>
                  {sideName(snapshot, participant.faction)} ·{' '}
                  <span className="card-level">{level}</span>
                </p>
                <strong>Expert rating {participant.main}</strong>
                <small className="card-roster">
                  General roster rating—not this fight’s win chance
                </small>
                <small className="card-roster">
                  {Math.round(snapshot.ctx.roster[participant.character]?.hitPoints ?? 0)} HP ·{' '}
                  {Math.round(snapshot.ctx.roster[participant.character]?.attack.damage ?? 0)}{' '}
                  damage · speed{' '}
                  {Math.round(
                    (snapshot.ctx.roster[participant.character]?.combat?.speed ?? 1) * 100,
                  )}
                  %
                </small>
                {chance !== undefined && (
                  <p data-testid="card-chance">
                    {Math.round(chance * 100)}% chance
                    {outcome ? ` · ${score!.won ? 'Winner' : 'Defeated'}` : ''}
                    <small>
                      {score?.source === 'measured'
                        ? 'Arena-measured baseline'
                        : 'Property estimate'}
                    </small>
                  </p>
                )}
              </article>
            );
          })}
        </div>
        {clash.phase === 'choose' ? (
          <>
            <OthersChoosing clash={clash} />
            <section className="card-hand" aria-label="Your support cards">
              <h2>
                {clash.chooser
                  ? `${sideName(snapshot, clash.chooser)}: choose one support`
                  : waitingFor(clash)}
                {clock && (
                  <small data-testid="card-clock">
                    {' '}
                    · {clock.held ? `stopped at ${clock.left} s` : `${clock.left} s left`}
                  </small>
                )}
                {clash.chooser && <small> · or conserve your hand</small>}
              </h2>
              {clock && <ClockBar key={`${clash.chooser}`} clock={clock} />}
              {/* Only the person choosing has a hand; nobody else has anything to press here. */}
              {clash.chooser && (
                <div className="support-cards">
                  {clash.hand.map((card) => (
                    <article className="battle-card support-card" key={card.id}>
                      <div className="card-portrait small">
                        {portraits[portraitOf(card.id)] ? (
                          <img src={portraits[portraitOf(card.id)]} alt={card.name} />
                        ) : (
                          <span>{card.name.slice(0, 1)}</span>
                        )}
                      </div>
                      <strong>{card.name}</strong>
                      <small>{card.fit}</small>
                      {card.water ? (
                        <div className="water-roles">
                          {(['pressure', 'protection', 'deception'] as const).map((role) => (
                            <button key={role} onClick={() => onCommit(card.id, role)}>
                              {role}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <button onClick={() => onCommit(card.id)}>{card.role}</button>
                      )}
                    </article>
                  ))}
                  <button className="no-support" onClick={() => onCommit(null)}>
                    No support
                  </button>
                </div>
              )}
            </section>
          </>
        ) : (
          <CardReveal
            snapshot={snapshot}
            clash={clash}
            portraits={portraits}
            portraitOf={(card) => portraitOf(card as SupportCardId)}
            clock={clock}
            onContinue={onContinue}
          />
        )}
      </div>
    </div>
  );
}

/** A support card's picture: the character's own, or the elemental's for an element. */
function portraitOf(card: SupportCardId): string {
  return card.startsWith('character:') ? card.slice(10) : `elemental-${card.slice(8)}`;
}

/**
 * The seconds left as a bar. It shrinks through each second to the length of the next, so it
 * runs smoothly between the clock's ticks and is empty when the time is up.
 */
function ClockBar({ clock }: { clock: ClockView }) {
  const target = clock.held ? clock.left : Math.max(0, clock.left - 1);
  const [shown, setShown] = useState(clock.left);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setShown(target));
    return () => cancelAnimationFrame(frame);
  }, [target]);
  return (
    <div
      className="card-clock"
      role="progressbar"
      aria-label="Time left to choose"
      aria-valuemin={0}
      aria-valuemax={clock.total}
      aria-valuenow={clock.left}
    >
      <span style={{ transform: `scaleX(${shown / clock.total})` }} />
    </div>
  );
}

/**
 * While a person chooses, the others' supports: face down once chosen, and a side still choosing
 * says so, the AI too, which takes a moment over its card as a player would (D-41).
 */
function OthersChoosing({ clash }: { clash: CardClashView }) {
  return (
    <div className="played-supports" aria-label="The others' supports">
      {clash.participants.map((p) =>
        p.faction === clash.chooser ? (
          <div key={p.pieceId} className="played-support mine" aria-hidden="true" />
        ) : clash.choosing?.includes(p.faction) ? (
          <div key={p.pieceId} className="played-support choosing" data-testid="side-choosing">
            <small>{p.name} is choosing…</small>
          </div>
        ) : (
          <div key={p.pieceId} className="played-support face-down" data-testid="side-chosen">
            <span>?</span>
          </div>
        ),
      )}
    </div>
  );
}

/** Who a person who has chosen is waiting for. */
function waitingFor(clash: CardClashView): string {
  const names = clash.participants
    .filter((p) => clash.choosing?.includes(p.faction))
    .map((p) => p.name);
  return names.length === 0
    ? 'Waiting for the others to choose'
    : `${names.join(' and ')} ${names.length === 1 ? 'is' : 'are'} choosing…`;
}
